import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getCSRFCookie, patch, post } from 'api/base';
import { isElectron } from 'utils/environment';
import type { User } from 'types/backend';
import { authKeys } from 'hooks/auth';
import { languageKeys } from 'hooks/languages';

export type UpdateMeParams = Partial<
  Pick<
    User,
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'startWithoutRecordingByDefault'
    | 'recordingDisclaimerExpanded'
    | 'additionalLanguages'
  >
>;
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdateMeParams) => {
      return patch('me', data) as Promise<User>;
    },
    onSuccess: (updatedUser: User, changes) => {
      qc.setQueryData(authKeys.me, updatedUser);
      if (changes.additionalLanguages) {
        void qc.invalidateQueries({ queryKey: languageKeys.list });
      }
    },
  });
}

export type ChangePasswordParams = { oldPassword: string; newPassword: string };
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: ChangePasswordParams) => {
      if (!isElectron) {
        // Only web browsers need CSRF token for change password
        await getCSRFCookie();
      }
      return post('changePassword', data);
    },
    onSuccess: () => {
      qc.setQueryData(authKeys.me, null);
      void qc.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}
